import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PencilLine,
  Trash2,
  Search,
  X,
  PlusCircle,
  UserCog2
} from 'lucide-react';
import AddRoleModal from './AddRoleModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import { roleService, RoleDefinition } from '../../../../components/api/roleService';
import { useAccessTheme } from './useAccessTheme';

interface UserRolesModalProps {
  onClose: () => void;
}

const UserRolesModal: React.FC<UserRolesModalProps> = ({ onClose }) => {
  const t = useAccessTheme();
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleToDelete, setRoleToDelete] = useState<RoleDefinition | null>(null);
  const [editRole, setEditRole] = useState<RoleDefinition | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const refreshRoles = async () => {
    setLoading(true);
    try {
      const res = await roleService.getRoleDefinitions();
      setRoles(res || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshRoles();
  }, []);

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    try {
      await roleService.deleteRole(roleToDelete.id);
      setRoles((prev) => prev.filter((r) => r.id !== roleToDelete.id));
    } catch (err) {
      console.error('Failed to delete role:', err);
    } finally {
      setRoleToDelete(null);
    }
  };

  const filteredRoles = roles.filter((role) =>
    role.roleName.toLowerCase().includes(search.toLowerCase()) ||
    role.description.toLowerCase().includes(search.toLowerCase()) ||
    role.id.toLowerCase().includes(search.toLowerCase()) ||
    (role.createdBy || '').toLowerCase().includes(search.toLowerCase())
  );

  return createPortal(
    <>
      <div className={'fixed inset-0 flex items-center justify-center px-4 py-6 ' + t.overlay} style={{ zIndex: 100 }}>
        <div className={'rounded-xl shadow-xl w-full max-w-4xl overflow-hidden border ' + t.panel}>
          <div className={'flex items-center justify-between px-5 py-4 border-b ' + t.header}>
            <div className="flex items-center gap-3">
              <div className={'w-10 h-10 rounded-lg flex items-center justify-center ' + t.iconBg}>
                <UserCog2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className={'text-lg font-semibold ' + t.title}>User Roles</h3>
                <p className={'text-xs ' + t.muted}>Define and manage user roles.</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className={'p-2 rounded-lg ' + t.closeBtn}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5">
            <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">
              <button
                type="button"
                onClick={() => { setEditRole(null); setShowRoleModal(true); }}
                className={'flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ' + t.btnPrimary}
              >
                <PlusCircle className="w-4 h-4" /> Add New Role
              </button>
              <div className="relative flex-1 max-w-md">
                <Search className={'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ' + t.muted} />
                <input
                  type="text"
                  placeholder="Search by name, id, or description..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={'w-full border rounded-lg pl-9 pr-3 py-2 text-sm outline-none ' + t.input}
                />
              </div>
            </div>

            <div className={'overflow-hidden border rounded-lg ' + t.tableShell}>
              <div className="overflow-x-auto max-h-[50vh]">
                <table className="w-full text-left text-sm">
                  <thead className={t.tableHead + ' text-xs uppercase tracking-wide'}>
                    <tr>
                      <th className="px-4 py-3">Role Name</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Admin</th>
                      <th className="px-4 py-3">Active</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className={'px-4 py-12 text-center ' + t.muted}>Loading roles...</td>
                      </tr>
                    ) : filteredRoles.length > 0 ? (
                      filteredRoles.map((role) => (
                        <tr key={role.id} className={'border-t ' + t.rowBorder + ' ' + t.rowHover}>
                          <td className="px-4 py-3">
                            <div className={'font-medium ' + t.title}>{role.roleName}</div>
                            <div className={'text-[10px] font-mono mt-0.5 truncate max-w-[160px] ' + t.muted} title={role.id}>
                              {role.id}
                            </div>
                          </td>
                          <td className={'px-4 py-3 max-w-[200px] ' + t.body}>
                            <span className="line-clamp-2">{role.description || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={'inline-flex px-2 py-0.5 rounded text-xs border ' + (role.isAdmin ? t.badgeAccent : t.badge)}>
                              {role.isAdmin ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={'inline-flex px-2 py-0.5 rounded text-xs border ' + (role.isActive ? t.badgeAccent : t.badge)}>
                              {role.isActive ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <button type="button" onClick={() => { setEditRole(role); setShowRoleModal(true); }}
                                className={'p-1.5 rounded ' + t.muted + ' ' + t.actionHover + ' hover:text-indigo-500'} title="Edit">
                                <PencilLine className="w-4 h-4" />
                              </button>
                              <button type="button" onClick={() => setRoleToDelete(role)}
                                className={'p-1.5 rounded ' + t.muted + ' ' + t.actionHover + ' hover:text-red-500'} title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className={'text-center py-12 ' + t.muted}>No roles found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className={'px-5 py-4 border-t flex justify-end ' + t.footer}>
            <button type="button" onClick={onClose} className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnSecondary}>
              Close
            </button>
          </div>
        </div>
      </div>

      {showRoleModal ? (
        <AddRoleModal
          role={editRole}
          onClose={() => setShowRoleModal(false)}
          onSubmit={() => { setShowRoleModal(false); refreshRoles(); }}
        />
      ) : null}

      {roleToDelete ? (
        <ConfirmDialog
          message={'Are you sure you want to permanently delete role "' + roleToDelete.roleName + '"? This action cannot be undone.'}
          onYes={handleDeleteRole}
          onNo={() => setRoleToDelete(null)}
          onCancel={() => setRoleToDelete(null)}
        />
      ) : null}
    </>,
    document.body
  );
};

export default UserRolesModal;
